import React, { useEffect, useState } from 'react';
import { Icon, ChannelBadge } from './inboxIcons';

// ---- helpers shared with the inbox list ----

const cleanPhone = (p) => String(p || '').replace(/\D/g, '');
const RESOLVED = new Set(['converted', 'ignored']);

// One row per customer: carts sharing a phone OR an email are merged, notes combined
export function groupCarts(rawList) {
  const parent = rawList.map((_, i) => i);
  const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const owner = new Map();
  rawList.forEach((c, i) => {
    const phone = cleanPhone(c.customer?.phone).slice(-10);
    const email = String(c.customer?.email || '').trim().toLowerCase();
    [phone.length === 10 && `phone:${phone}`, email && `email:${email}`].filter(Boolean).forEach((k) => {
      if (owner.has(k)) parent[find(i)] = find(owner.get(k)); else owner.set(k, i);
    });
  });
  const groups = new Map();
  rawList.forEach((c, i) => {
    const root = find(i);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(c);
  });
  return [...groups.values()].map((list) => {
    list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const seen = new Set();
    const notes = [];
    list.forEach((c) => (c.notes || []).forEach((n) => {
      const k = `${n.agentId || ''}_${n.timestamp || ''}_${n.text || ''}`;
      if (!seen.has(k)) { seen.add(k); notes.push(n); }
    }));
    notes.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return { ...list[0], notes };
  });
}

export const cartStatus = (c) => {
  const s = c.recoveryStatus || 'pending';
  return s === 'called' || s === 'whatsapp_sent' ? 'pending' : s;
};

// Due = open and either its follow-up time has come, or it's been 2h since the cart was left
export function isDue(c, now = Date.now()) {
  if (RESOLVED.has(c.recoveryStatus)) return false;
  if (c.followUpAt) return new Date(c.followUpAt).getTime() <= now;
  return now - new Date(c.createdAt).getTime() >= 2 * 3600e3;
}

// When the cart is (or was) due: its follow-up time, else 2 hours after it was left
export const cartDueMs = (c) => (c.followUpAt ? new Date(c.followUpAt).getTime() : new Date(c.createdAt).getTime() + 2 * 3600e3);

export const cartName = (c) => [c.customer?.firstName, c.customer?.lastName].filter(Boolean).join(' ') || c.customer?.email || cleanPhone(c.customer?.phone) || 'Guest';
export const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const waLink = (phone) => {
  const d = cleanPhone(phone);
  return d ? `https://wa.me/${d.length === 10 ? `91${d}` : d}` : null;
};

const pad = (n) => String(n).padStart(2, '0');
const localInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const dayAt = (days, hour = 11) => { const d = new Date(Date.now() + days * 864e5); d.setHours(hour, 0, 0, 0); return d; };
const shortDay = (ms) => new Date(ms).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

function since(dateStr) {
  const h = Math.floor((Date.now() - new Date(dateStr).getTime()) / 3600e3);
  return h < 1 ? 'just now' : h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

// What happened on a follow-up (counted in Analytics); the last two close the cart
const HOW = [['call', 'Call'], ['whatsapp', 'WhatsApp'], ['email', 'Email'], ['note', 'Note']];
const RESULTS = [['NO_ANSWER', 'No answer'], ['WILL_ORDER', 'Will order'], ['NEEDS_HELP', 'Needs help'], ['PRICE', 'Price / discount'], ['NOT_INTERESTED', 'Not interested'], ['WRONG_NUMBER', 'Wrong number']];
const CLOSING = new Set(['NOT_INTERESTED', 'WRONG_NUMBER']);
const IGNORE_REASONS = [['NOT_INTERESTED', 'Not interested'], ['WRONG_NUMBER', 'Wrong number'], ['DUPLICATE', 'Duplicate cart'], ['TEST', 'Test / our team'], ['OTHER', 'Other']];
const NEXT = [['1', 'Tomorrow'], ['3', 'In 3 days'], ['pick', 'Pick date'], ['none', 'No more follow-up']];
const ICON = { call: 'phone', whatsapp: 'whatsapp', message: 'chat', email: 'mail', note: 'edit', link: 'link', done: 'check' };
const TICK = { sent: 'sent', delivered: 'delivered', read: 'read', failed: 'not delivered' };

// ---- detail pane ----

function CartPhoto({ src, loading }) {
  const [broken, setBroken] = useState(false);
  if (loading) return <span className="cx-cart-photo cx-skel-in" aria-hidden="true" />;
  if (!src || broken) return <span className="cx-cart-photo none" aria-hidden="true"><Icon name="shop" size={18} /></span>;
  return <img className="cx-cart-photo" src={src} alt="" width={48} height={48} loading="lazy" onError={() => setBroken(true)} />;
}

/** One line per event: automatic emails and WhatsApp reminders short, team follow-ups with their result and note. */
function historyRows(cart, activity) {
  const rows = [];
  (cart.notes || []).forEach((n) => {
    const at = new Date(n.timestamp).getTime();
    const text = String(n.text || '');
    const step = text.match(/Step (\d)/);
    if (n.agentId === 'system' && /recovery email/i.test(text)) rows.push({ at, icon: 'mail', title: `Recovery email ${step ? step[1] : ''}`.trim(), sub: 'automatic' });
    else if (n.agentId === 'system' && /WhatsApp reminder \d sent/.test(text)) { if (!activity) rows.push({ at, icon: 'whatsapp', title: text.replace(/ sent.*/, ''), sub: 'automatic' }); }
    else if (n.agentId === 'system') rows.push({ at, icon: /Ordered|converted/i.test(text) ? 'done' : 'edit', title: text, sub: '' });
    else rows.push({ at, icon: ICON[n.type] || 'edit', title: `${{ call: 'Call', whatsapp: 'WhatsApp', message: 'Message', email: 'Email' }[n.type] || 'Note'}${n.resultLabel ? ` · ${n.resultLabel}` : ''}`, sub: (n.agentName || 'Team').split('@')[0], text: n.resultLabel && text === n.resultLabel ? '' : text });
  });
  (activity?.reminders || []).forEach((r) => r.atMs && rows.push({ at: r.atMs, icon: 'whatsapp', title: r.manual ? 'WhatsApp reminder (team)' : `WhatsApp reminder ${r.step || ''}`.trim(), sub: r.waStatus ? TICK[r.waStatus] || r.waStatus : r.status === 'failed' ? 'not sent' : 'sent', bad: r.waStatus === 'failed' || r.status === 'failed' }));
  (activity?.linkOpens || []).forEach((l) => l.firstMs && rows.push({ at: l.firstMs, icon: 'link', title: `Opened the checkout link${l.clicks > 1 ? ` (${l.clicks}×)` : ''}`, sub: l.manual ? 'from the team reminder' : l.step ? `from reminder ${l.step}` : '', good: true }));
  return rows.sort((a, b) => a.at - b.at);
}

function InboxCart({ cart, api, onChanged, onBack, toast, onCustomer = null }) {
  const blank = { how: '', result: '', text: '', next: '', pick: '' };
  const [form, setForm] = useState(blank);
  const [ignoring, setIgnoring] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [lines, setLines] = useState(null); // items with product photo + title + stock/price now, loaded per cart
  const [activity, setActivity] = useState(null);
  const [allItems, setAllItems] = useState(false);

  const cartId = cart?.id;
  useEffect(() => {
    if (!cartId) return undefined;
    let alive = true;
    setLines(null); setActivity(null); setForm(blank); setIgnoring(false); setError(null); setAllItems(false);
    api(`/api/v1/support/carts/${encodeURIComponent(cartId)}/items`).then((r) => alive && setLines(r.items || [])).catch(() => alive && setLines(false));
    api(`/api/v1/support/carts/${encodeURIComponent(cartId)}/activity`).then((r) => alive && setActivity(r)).catch(() => alive && setActivity(false));
    return () => { alive = false; };
  }, [api, cartId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cart) return <section className="cx-thread"><div className="cx-empty"><strong>Select a cart</strong>Pick a customer on the left to follow up.</div></section>;

  const status = cartStatus(cart);
  const value = cart.cart?.totalPrice || 0;
  const items = cart.cart?.lineItems || [];
  const cooling = Date.now() - new Date(cart.createdAt).getTime() < 2 * 3600e3;
  const wa = waLink(cart.customer?.phone);
  const shown = lines || items.map((it) => ({ title: it.title || it.sku || 'Item', sku: it.sku, quantity: it.quantity, price: Number(it.price || 0), lineTotal: Number(it.price || 0) * Number(it.quantity || 0), image: null, loading: lines === null }));
  const visible = allItems ? shown : shown.slice(0, 3);
  const problems = (lines || []).filter((l) => l.inactive || l.stock === 0 || l.priceChanged).length;
  const history = historyRows(cart, activity || null);
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setError(null); };

  const patch = async (body, done, key = 'save') => {
    setBusy(key); setError(null);
    try {
      await api(`/api/v1/support/abandoned-checkouts/${encodeURIComponent(cart.id)}`, 'PATCH', body);
      toast(done); onChanged();
      return true;
    } catch (err) { setError(err.message); return false; } finally { setBusy(null); }
  };

  const closes = CLOSING.has(form.result);
  const save = async () => {
    if (!form.how) { setError('Choose how you followed up.'); return; }
    if (!form.result && !form.text.trim()) { setError('Pick what happened or write a note.'); return; }
    if (!closes && !form.next) { setError('Choose when to follow up next.'); return; }
    if (form.next === 'pick' && !form.pick) { setError('Pick the next follow-up date.'); return; }
    const body = { noteType: form.how, noteResult: form.result || undefined, noteText: form.text.trim() || undefined };
    if (!closes) {
      if (form.next === 'none') Object.assign(body, { recoveryStatus: 'ignored', ignoreReason: 'NO_MORE_FOLLOW_UP', followUpAt: null });
      else body.followUpAt = form.next === 'pick' ? new Date(form.pick).toISOString() : dayAt(Number(form.next)).toISOString();
    }
    const ok = await patch(body, closes || form.next === 'none' ? 'Saved. Cart closed.' : 'Follow-up saved');
    if (ok) setForm(blank);
  };

  const remind = async () => {
    setBusy('remind'); setError(null);
    try {
      const r = await api(`/api/v1/support/carts/${encodeURIComponent(cart.id)}/remind`, 'POST', {});
      toast(r.how === 'template' ? 'Cart reminder sent on WhatsApp' : 'Checkout link sent on WhatsApp');
      onChanged();
      api(`/api/v1/support/carts/${encodeURIComponent(cart.id)}/activity`).then(setActivity).catch(() => {});
    } catch (err) { setError(err.message); } finally { setBusy(null); }
  };

  const copy = () => {
    if (!cart.abandonedCheckoutUrl) return;
    navigator.clipboard?.writeText(cart.abandonedCheckoutUrl).then(() => toast('Recovery link copied')).catch(() => toast('Copy failed. Select the link manually.', true));
  };

  return (
    <section className="cx-thread" aria-label={`Cart from ${cartName(cart)}`}>
      <header className="cx-t-head">
        <button className="cx-btn cx-btn-icon cx-btn-ghost cx-back" onClick={onBack} aria-label="Back to list"><Icon name="back" /></button>
        <ChannelBadge channel="cart" large />
        <div className="cx-t-who"><b>{cartName(cart)}</b><span>Abandoned cart · {since(cart.createdAt)} · {(cart.source || 'Shopify')}</span></div>
        <div className="cx-t-actions">
          {cart.customer?.phone && <a className="cx-btn" href={`tel:${cart.customer.phone}`} onClick={() => set({ how: 'call' })}><Icon name="phone" />Call</a>}
          {cart.customer?.phone && status === 'pending' && <button className="cx-btn cx-hide-sm" onClick={remind} disabled={!!busy || !cart.abandonedCheckoutUrl} title="Sends the checkout link on WhatsApp from our business number and logs it here"><Icon name="whatsapp" />{busy === 'remind' ? 'Sending…' : 'Send reminder'}</button>}
          <button className="cx-btn cx-hide-sm" onClick={copy} disabled={!cart.abandonedCheckoutUrl}><Icon name="link" />{cart.abandonedCheckoutUrl ? 'Copy link' : 'No link'}</button>
          {wa && <a className="cx-btn cx-btn-icon cx-btn-ghost cx-hide-sm" href={wa} target="_blank" rel="noreferrer" title="Open in WhatsApp (not logged)" aria-label="Open in WhatsApp"><Icon name="more" /></a>}
          {onCustomer && <button className="cx-btn cx-btn-icon cx-ctx-toggle" onClick={onCustomer} aria-label="Customer details"><Icon name="user" /></button>}
        </div>
      </header>
      {cooling && status === 'pending' && <div className="cx-banner"><Icon name="clock" />Left less than 2 hours ago. Give them time before following up.</div>}
      {cart.convertedOrderNumber && <div className="cx-banner cx-cart-won"><Icon name="check" />Ordered: <b className="cx-mono">#{cart.convertedOrderNumber}</b>{cart.convertedBy === 'support_reconcile' ? ' · marked converted automatically' : ''}</div>}
      <div className="cx-msgs">
        <div className="cx-cart-facts">
          <span><ChannelBadge channel="email" />{cart.customer?.email || 'No email'}</span>
          <span><Icon name="phone" size={13} />{cart.customer?.phone || 'No phone'}</span>
          <span><Icon name="clock" size={13} />Left {new Date(cart.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</span>
          {cart.followUpAt && status === 'pending' && <span><Icon name="calendar" size={13} />Next follow-up {new Date(cart.followUpAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</span>}
        </div>
        <div className="cx-cart">
          <div className="cx-cart-head"><b>{items.length} item{items.length === 1 ? '' : 's'} · <span className="cx-mono">{inr(value)}</span></b>{problems > 0 && <span className="cx-chip cx-c-new">{problems} changed since</span>}<span className="cx-spacer" />{cart.abandonedCheckoutUrl && <a href={cart.abandonedCheckoutUrl} target="_blank" rel="noreferrer" className="cx-linkbtn">Open checkout</a>}</div>
          {visible.map((it, i) => (
            <div className="cx-cart-line" key={`${it.sku || 'x'}-${i}`}>
              <CartPhoto src={it.image} loading={it.loading} />
              <div className="cx-cart-info">
                {it.url ? <a className="cx-cart-name" href={it.url} target="_blank" rel="noreferrer">{it.title}</a> : <span className="cx-cart-name">{it.title}</span>}
                <span className="cx-cart-meta">
                  {it.inactive ? <span className="cx-chip cx-c-new">No longer sold</span> : it.stock === 0 ? <span className="cx-chip cx-c-new">Out of stock</span> : it.stock != null && it.stock < it.quantity ? <span className="cx-chip cx-c-waiting">Only {it.stock} in stock</span> : null}
                  {it.priceChanged && <span className="cx-chip cx-c-waiting">Price now {inr(it.priceNow)}</span>}
                  {it.sku && <span className="cx-mono">{it.sku}</span>}
                </span>
              </div>
              <div className="cx-cart-qty"><span className="cx-mono">{it.quantity} × {inr(it.price)}</span><b className="cx-mono">{inr(it.lineTotal)}</b></div>
            </div>
          ))}
          {shown.length > 3 && <button className="cx-linkbtn cx-cart-more" onClick={() => setAllItems((v) => !v)}>{allItems ? 'Show fewer' : `+${shown.length - 3} more item${shown.length - 3 === 1 ? '' : 's'}`}</button>}
        </div>
        {history.length > 0 && (
          <div style={{ alignSelf: 'stretch' }}>
            <div className="cx-vgroup-label" style={{ margin: '4px 0 8px' }}>History</div>
            <ol className="cx-cart-hist">
              {history.map((h, i) => (
                <li key={i} className={h.good ? 'good' : h.bad ? 'bad' : ''}>
                  <Icon name={h.icon === 'done' ? 'check' : h.icon} size={14} />
                  <span><b>{h.title}</b>{h.sub ? <span className="cx-hint"> · {h.sub}</span> : ''}{h.text ? <span className="cx-cart-hist-text">{h.text}</span> : ''}</span>
                  <span className="cx-hint cx-cart-hist-at">{shortDay(h.at)}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
      <div className="cx-dock">
        {status === 'pending' && !ignoring && (
          <div className="cx-composer cx-cart-log" role="group" aria-label="Log a follow-up">
            <div className="cx-cart-log-row"><span className="cx-cart-log-k">How</span><div className="cx-cart-chips">{HOW.map(([k, l]) => <button key={k} type="button" aria-pressed={form.how === k} onClick={() => set({ how: k })}>{l}</button>)}</div></div>
            <div className="cx-cart-log-row"><span className="cx-cart-log-k">Result</span><div className="cx-cart-chips">{RESULTS.map(([k, l]) => <button key={k} type="button" aria-pressed={form.result === k} onClick={() => set({ result: form.result === k ? '' : k })}>{l}</button>)}</div></div>
            <input className="cx-cart-log-note" type="text" maxLength={1000} value={form.text} onChange={(e) => set({ text: e.target.value })} placeholder="Note (optional), e.g. asked about COD, will order tonight" aria-label="Follow-up note" />
            <div className="cx-cart-log-row">
              <span className="cx-cart-log-k">Next</span>
              {closes ? <span className="cx-hint">This closes the cart (Ignored: {RESULTS.find(([k]) => k === form.result)[1].toLowerCase()}).</span> : (
                <div className="cx-cart-chips">
                  {NEXT.map(([k, l]) => <button key={k} type="button" aria-pressed={form.next === k} onClick={() => set({ next: k, pick: k === 'pick' ? (form.pick || localInput(dayAt(2))) : form.pick })}>{l}</button>)}
                  {form.next === 'pick' && <input type="datetime-local" value={form.pick} onChange={(e) => set({ pick: e.target.value })} aria-label="Next follow-up date" className="cx-cart-pick" />}
                </div>
              )}
              <span className="cx-spacer" />
              <button className="cx-btn cx-btn-primary" onClick={save} disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        )}
        {ignoring && (
          <div className="cx-composer cx-cart-log" role="group" aria-label="Why ignore this cart?">
            <div className="cx-cart-log-row"><span className="cx-cart-log-k">Why?</span><div className="cx-cart-chips">{IGNORE_REASONS.map(([k, l]) => <button key={k} type="button" onClick={async () => { if (await patch({ recoveryStatus: 'ignored', ignoreReason: k, followUpAt: null }, 'Cart ignored', 'ignore')) setIgnoring(false); }} disabled={!!busy}>{l}</button>)}</div><span className="cx-spacer" /><button className="cx-btn cx-btn-ghost" onClick={() => setIgnoring(false)}>Cancel</button></div>
          </div>
        )}
        {error && <div className="cx-err">{error}</div>}
        <div className="cx-draft-foot" style={{ border: '1px solid var(--line)', borderRadius: 12, background: 'var(--surface)' }}>
          <span className="cx-then">Status</span>
          <button className={`cx-btn cx-btn-sm${status === 'pending' ? ' cx-btn-primary' : ''}`} onClick={() => status !== 'pending' && patch({ recoveryStatus: 'pending' }, 'Back to pending', 'status')} disabled={!!busy}>Pending</button>
          <button className={`cx-btn cx-btn-sm${status === 'converted' ? ' cx-btn-primary' : ''}`} onClick={() => status !== 'converted' && patch({ recoveryStatus: 'converted', followUpAt: null }, 'Marked as converted', 'status')} disabled={!!busy}>Converted</button>
          <button className={`cx-btn cx-btn-sm${status === 'ignored' ? ' cx-btn-primary' : ''}`} onClick={() => status !== 'ignored' && setIgnoring(true)} disabled={!!busy}>Ignore</button>
          {status === 'ignored' && cart.ignoreReason && <span className="cx-hint">{(IGNORE_REASONS.find(([k]) => k === cart.ignoreReason) || [null, cart.ignoreReason === 'NO_MORE_FOLLOW_UP' ? 'no more follow-up' : cart.ignoreReason])[1]}</span>}
        </div>
      </div>
    </section>
  );
}

export default InboxCart;
